import * as React from 'react';
import {
  Box,
  SwipeableDrawer,
  Typography,
  Divider,
  FormControl,
  TextField,
  Button,
  IconButton,
  Stack,
  Slider,
  Autocomplete,
  Chip,
  useTheme,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from 'react-router-dom';
// ✅ Import Encoder/Decoder
import { encodeId, decodeId } from '../utils/idEncoder';

// --- Types ---
interface FilterOption {
  [key: string]: any;
  description?: string;
  dealerCompanyName?: string;
}

type RightDrawerProps = {
  open: boolean;
  onClose: () => void;
  fuelTypeData?: any[];
  TransmissionTypeData?: any[];
  bodyTypesData?: any[];
  brandData?: any[];
  dealerData?: any[];
};

type SelectedFilters = {
  vehicleFuelTypeIds: string[];
  vehicleTransmissionTypeIds: string[];
  vehicleBodyTypeIds: string[];
  vehicleBrandIds: string[];
  dealerIds: string[];
  minAskingPrice: string;
  maxAskingPrice: string;
  minManufacturingYear: string;
  maxManufacturingYear: string;
  minMileage: string;
  maxMileage: string;
};

// Define keys that should be encrypted
const ENCRYPTED_KEYS = [
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleFuelTypeIds',
    'vehicleTransmissionTypeIds',
    'dealerIds'
];

const RightDrawer: React.FC<RightDrawerProps> = ({
  open,
  onClose,
  fuelTypeData = [],
  TransmissionTypeData = [],
  bodyTypesData = [],
  brandData = [],
  dealerData = [],
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // --- State ---
  const [selectedFilters, setSelectedFilters] = React.useState<SelectedFilters>({
    vehicleFuelTypeIds: [],
    vehicleTransmissionTypeIds: [],
    vehicleBodyTypeIds: [],
    vehicleBrandIds: [],
    dealerIds: [],
    minAskingPrice: '',
    maxAskingPrice: '',
    minManufacturingYear: '',
    maxManufacturingYear: '',
    minMileage: '',
    maxMileage: '',
  });

  // Initialize state when drawer opens
  React.useEffect(() => {
    if (open) {
      initializeFiltersFromURL();
    }
  }, [open]);

  const initializeFiltersFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Helper to get and potentially decode array params
    const getArrayParam = (key: string) => {
        const raw = searchParams.get(key);
        if (!raw) return [];
        const parts = raw.split(',').map((v) => v.trim()).filter(Boolean);

        // ✅ DECODE if it's an encrypted key
        if (ENCRYPTED_KEYS.includes(key)) {
            return parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
        }
        return parts;
    };

    setSelectedFilters({
      vehicleFuelTypeIds: getArrayParam('vehicleFuelTypeIds'),
      vehicleTransmissionTypeIds: getArrayParam('vehicleTransmissionTypeIds'),
      vehicleBodyTypeIds: getArrayParam('vehicleBodyTypeIds'),
      vehicleBrandIds: getArrayParam('vehicleBrandIds'),
      dealerIds: getArrayParam('dealerIds'),
      minAskingPrice: searchParams.get('minAskingPrice') || '',
      maxAskingPrice: searchParams.get('maxAskingPrice') || '',
      minManufacturingYear: searchParams.get('minManufacturingYear') || '',
      maxManufacturingYear: searchParams.get('maxManufacturingYear') || '',
      minMileage: searchParams.get('minMileage') || '',
      maxMileage: searchParams.get('maxMileage') || '',
    });
  };

  const applyFilters = () => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
           // ✅ ENCODE if it's an encrypted key before setting URL
           if (ENCRYPTED_KEYS.includes(key)) {
               const encodedValues = value.map(id => encodeId(id));
               currentParams.set(key, encodedValues.join(','));
           } else {
               currentParams.set(key, value.join(','));
           }
        } else {
          currentParams.delete(key);
        }
      } else if (value !== '' && value !== null) {
        currentParams.set(key, String(value));
      } else {
        currentParams.delete(key);
      }
    });

    currentParams.delete('page');
    navigate(`/vehicle-listing?${currentParams.toString()}`);
    onClose();
  };

  const resetFilters = () => {
    setSelectedFilters({
      vehicleFuelTypeIds: [],
      vehicleTransmissionTypeIds: [],
      vehicleBodyTypeIds: [],
      dealerIds: [],
      vehicleBrandIds: [],
      minAskingPrice: '',
      maxAskingPrice: '',
      minManufacturingYear: '',
      maxManufacturingYear: '',
      minMileage: '',
      maxMileage: '',
    });
    navigate('/vehicle-listing');
    onClose();
  };

  // --- Render Helpers ---

  const FilterSectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {children}
    </Typography>
  );

  const renderAutocomplete = (
    label: string,
    key: keyof SelectedFilters,
    data: any[],
    idField: string,
    labelField: string,
    imageField?: string
  ) => {
    const selectedValues = data.filter((item) =>
      (selectedFilters[key] as string[]).includes(String(item[idField]))
    );

    return (
      <Box mb={3}>
        <FilterSectionLabel>{label}</FilterSectionLabel>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={data}
          value={selectedValues}
          getOptionLabel={(option) => option[labelField]}
          isOptionEqualToValue={(option, value) => String(option[idField]) === String(value[idField])}
          onChange={(_, newValue) =>
            setSelectedFilters({
              ...selectedFilters,
              [key]: newValue.map((val) => String(val[idField])),
            })
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              placeholder={selectedValues.length === 0 ? "Select options" : ""}
              size="small" 
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ padding: '6px 16px' }}>
              <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  {imageField && option[imageField] && (
                    <Box 
                      component="img" 
                      src={option[imageField]} 
                      sx={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain' }}
                    />
                  )}
                  <Typography variant="body2">{option[labelField]}</Typography>
                </Box>
                {selected && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option[labelField]}
                size="small"
                {...getTagProps({ index })}
                sx={{ borderRadius: 1, backgroundColor: '#f0f0f0', fontWeight: 500 }}
              />
            ))
          }
        />
      </Box>
    );
  };

  const renderRangeSelect = (
    label: string,
    options: { label: string; min: number; max: number | null }[],
    minKey: keyof SelectedFilters,
    maxKey: keyof SelectedFilters
  ) => {
    const currentValue = options.find(
      (opt) =>
        selectedFilters[minKey] === opt.min.toString() &&
        selectedFilters[maxKey] === (opt.max !== null ? opt.max.toString() : '')
    ) || null;

    return (
      <Box mb={3}>
        <FilterSectionLabel>{label}</FilterSectionLabel>
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option.label}
          value={currentValue}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          onChange={(_, newValue) => {
            if (newValue) {
              setSelectedFilters((prev) => ({
                ...prev,
                [minKey]: newValue.min.toString(),
                [maxKey]: newValue.max !== null ? newValue.max.toString() : '',
              }));
            } else {
              setSelectedFilters((prev) => ({
                ...prev,
                [minKey]: '',
                [maxKey]: '',
              }));
            }
          }}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Any" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />}
        />
      </Box>
    );
  };

  const renderSlider = (
    label: string,
    minKey: keyof SelectedFilters,
    maxKey: keyof SelectedFilters,
    min: number,
    max: number
  ) => {
    const minValue = Number(selectedFilters[minKey]) || min;
    const maxValue = Number(selectedFilters[maxKey]) || max;

    return (
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <FilterSectionLabel>{label}</FilterSectionLabel>
          <Typography variant="caption" color="primary.main" fontWeight="bold">
            {minValue} - {maxValue}
          </Typography>
        </Box>
        <Box px={1}>
          <Slider
            value={[minValue, maxValue]}
            min={min}
            max={max}
            onChange={(_, newValue) => {
              const [newMin, newMax] = newValue as number[];
              setSelectedFilters((prev) => ({
                ...prev,
                [minKey]: newMin.toString(),
                [maxKey]: newMax.toString(),
              }));
            }}
            valueLabelDisplay="auto"
            size="small"
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': { boxShadow: 1 },
            }}
          />
        </Box>
      </Box>
    );
  };

  // --- Constants ---
  const priceOptions = [
    { label: 'Under $30,000', min: 0, max: 30000 },
    { label: '$30,000 - $50,000', min: 30000, max: 50000 },
    { label: '$50,000 - $100,000', min: 50000, max: 100000 },
    { label: 'Above $100,000', min: 100000, max: null },
  ];

  const mileageOptions = [
    { label: 'Under 30,000 km', min: 0, max: 30000 },
    { label: '30,000 - 70,000 km', min: 30000, max: 70000 },
    { label: 'Above 70,000 km', min: 70000, max: null },
  ];

  return (
    <SwipeableDrawer 
      anchor="right" 
      open={open} 
      onClose={onClose} 
      onOpen={initializeFiltersFromURL}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } } // Full width on mobile, 400px on desktop
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        
        {/* --- Header --- */}
        <Box
          px={3}
          py={2}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight="bold">
              All Filters
            </Typography>
            <Box 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'black', 
                borderRadius: '50%', 
                width: 24, 
                height: 24, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 12,
                fontWeight: 'bold'
              }}
            >
              {/* Calculate rough number of active filters */}
              {Object.values(selectedFilters).filter(v => (Array.isArray(v) ? v.length > 0 : v !== '')).length}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f5f5f5' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* --- Scrollable Body --- */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflowY: 'auto', 
            p: 3,
            // Custom Scrollbar styling
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
          }}
        >
          {/* Main Filters */}
          {renderAutocomplete('Brand', 'vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
          {renderAutocomplete('Dealer', 'dealerIds', dealerData, 'dealerCompanyId', 'dealerCompanyName', 'imageUrl')}
          
          <Divider sx={{ my: 2 }} />
          
          {renderRangeSelect('Price Range', priceOptions, 'minAskingPrice', 'maxAskingPrice')}
          {renderSlider('Manufacturing Year', 'minManufacturingYear', 'maxManufacturingYear', 2000, new Date().getFullYear())}
          
          <Divider sx={{ my: 2 }} />

          {renderAutocomplete('Body Type', 'vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
          {renderAutocomplete('Fuel Type', 'vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
          {renderAutocomplete('Transmission', 'vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
          {renderRangeSelect('Mileage', mileageOptions, 'minMileage', 'maxMileage')}
        </Box>

        {/* --- Footer Actions --- */}
        <Box
          p={2}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.05)'
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="inherit" // Dark grey/black
              fullWidth
              startIcon={<RestartAltIcon />}
              onClick={resetFilters}
              sx={{ 
                borderRadius: 2, 
                borderColor: '#ddd', 
                color: 'text.secondary',
                '&:hover': { borderColor: 'text.primary', color: 'text.primary', bgcolor: 'transparent' }
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={applyFilters}
              sx={{ 
                borderRadius: 2, 
                fontWeight: 700,
                color: '#000', // Ensure text is black for contrast on primary yellow
              }}
            >
              Show Results
            </Button>
          </Stack>
        </Box>

      </Box>
    </SwipeableDrawer>
  );
};

export default RightDrawer;